// src/pages/admin/SystemSettings.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Settings, Save, Bell, Mail, Clock, Shield,
  Database, Globe, Palette, AlertTriangle,
  ToggleLeft, ToggleRight, Info, Wrench, RefreshCw, HardDrive, Server, Upload
} from 'lucide-react';
import { Button, Card, Breadcrumb, Input, Select, Textarea } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { getAssetUrl } from '../../utils/url';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';

interface SettingGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const settingGroups: SettingGroup[] = [
  { id: 'general', label: 'Umum', icon: <Globe className="w-5 h-5" />, description: 'Pengaturan dasar sistem' },
  { id: 'notification', label: 'Notifikasi', icon: <Bell className="w-5 h-5" />, description: 'Konfigurasi notifikasi & email' },
  { id: 'workflow', label: 'Workflow', icon: <Clock className="w-5 h-5" />, description: 'Aturan workflow dan SLA' },
  { id: 'security', label: 'Keamanan', icon: <Shield className="w-5 h-5" />, description: 'Kebijakan keamanan akun' },
  { id: 'appearance', label: 'Tampilan', icon: <Palette className="w-5 h-5" />, description: 'Customisasi tampilan sistem' },
  { id: 'maintenance', label: 'Maintenance', icon: <Wrench className="w-5 h-5" />, description: 'Informasi sistem & pemeliharaan' },
];

const SystemSettings: React.FC = () => {
  const [activeGroup, setActiveGroup] = useState('general');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // System Info State
  const [systemInfo, setSystemInfo] = useState<any>(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Settings state - Initializer
  const [settings, setSettings] = useState<Record<string, any>>({
    // General
    app_name: 'SIM-EVAL UBP Karawang',
    app_description: 'Sistem Informasi Manajemen Evaluasi',
    institution_name: 'Universitas Buana Perjuangan Karawang',
    institution_code: 'UBP-KRW',
    institution_address: 'Jl. HS. Ronggowaluyo, Telukjambe Timur, Karawang',
    institution_phone: '(0267) 8404010',
    institution_email: 'info@ubpkarawang.ac.id',
    institution_website: 'https://www.ubpkarawang.ac.id',

    // Notification
    email_enabled: true,
    email_from: 'noreply@simeval.ubpkarawang.ac.id',
    email_from_name: 'SIM-EVAL UBP Karawang',
    notif_submission: true,
    notif_validation: true,
    notif_rapim: true,
    notif_overdue: true,
    notif_reminder_days: '7,3,1',

    // Workflow
    sla_rendah: 60,
    sla_sedang: 30,
    sla_tinggi: 14,
    sla_critical: 7,
    auto_escalation: true,
    escalation_after_days: 3,
    auto_archive_days: 90,
    max_file_size: 10,
    max_files_per_report: 10,

    // Security
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_number: true,
    password_require_special: true,
    session_lifetime: 120,

    // Appearance
    primary_color: '#0f33ab',
    logo_url: '',
    favicon_url: '',
    login_background: '',
    footer_text: '© 2026 SIM-EVAL UBP Karawang. All rights reserved.',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeGroup === 'maintenance') {
      fetchSystemInfo();
    }
  }, [activeGroup]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await adminService.settings.getAll();
      const newSettings: Record<string, any> = { ...settings };
      res.forEach(item => {
        if (item.type === 'boolean') {
          newSettings[item.key] = item.value === '1' || item.value === 'true';
        } else if (item.type === 'number' || item.type === 'int') {
          newSettings[item.key] = Number(item.value);
        } else {
          newSettings[item.key] = item.value;
        }
      });
      setSettings(newSettings);
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemInfo = async () => {
    try {
      const info = await adminService.system.getInfo();
      setSystemInfo(info);
    } catch (e) {
      console.error(e);
    }
  };

  const updateSetting = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const batchData = Object.keys(settings).map(key => ({
        key,
        value: typeof settings[key] === 'boolean' ? (settings[key] ? '1' : '0') : String(settings[key])
      }));

      await adminService.settings.updateBatch(batchData);

      // Update store also to reflect changes in sidebar/navbar immediately
      const { updateSettings } = useSystemSettingsStore.getState();
      updateSettings({
        app_name: settings.app_name,
        app_description: settings.app_description,
        institution_name: settings.institution_name,
        institution_code: settings.institution_code,
        institution_address: settings.institution_address,
        institution_phone: settings.institution_phone,
        institution_email: settings.institution_email,
        institution_website: settings.institution_website,
        logo_url: settings.logo_url,
        favicon_url: settings.favicon_url,
        footer_text: settings.footer_text,
      });

      toast.success('Pengaturan berhasil disimpan');
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await adminService.settings.sendTestEmail();
      toast.success(res?.message || 'Email uji coba berhasil dikirim');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal mengirim email uji coba');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleMaintenanceAction = async (action: 'clearCache' | 'migrate' | 'backup') => {
    if (!window.confirm('Anda yakin ingin menjalankan tindakan ini?')) return;

    setMaintenanceLoading(true);
    try {
      if (action === 'clearCache') await adminService.system.clearCache();
      if (action === 'migrate') await adminService.system.runMigrations();
      if (action === 'backup') await adminService.system.backup();

      toast.success('Tindakan berhasil dijalankan');
      if (action !== 'backup') fetchSystemInfo();
    } catch (e) {
      toast.error('Gagal menjalankan tindakan');
    } finally {
      setMaintenanceLoading(false);
    }
  };



  const validatePngFile = (file: File, label: string, maxMb = 2) => {
    if (file.type !== 'image/png') {
      toast.error(`${label} harus PNG transparan`);
      return false;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Ukuran ${label.toLowerCase()} maksimal ${maxMb}MB`);
      return false;
    }
    return true;
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validatePngFile(file, 'Logo')) {
      e.target.value = '';
      return;
    }

    const toastId = toast.loading('Mengunggah logo...');
    setUploadingLogo(true);
    try {
      const res = await adminService.settings.uploadLogo(file);
      const newUrl = res?.data?.logo_url || res?.logo_url;
      if (newUrl) {
        updateSetting('logo_url', newUrl);
        // Sync store
        const { updateSettings } = useSystemSettingsStore.getState();
        updateSettings({ logo_url: newUrl });
      }
      toast.success('Logo berhasil diperbarui', { id: toastId });
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Gagal mengunggah logo';
      toast.error(message, { id: toastId });
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleFaviconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validatePngFile(file, 'Favicon')) {
      e.target.value = '';
      return;
    }

    const toastId = toast.loading('Mengunggah favicon...');
    setUploadingFavicon(true);
    try {
      const res = await adminService.settings.uploadFavicon(file);
      const newUrl = res?.data?.favicon_url || res?.favicon_url;
      if (newUrl) {
        updateSetting('favicon_url', newUrl);
        // Sync store
        const { updateSettings } = useSystemSettingsStore.getState();
        updateSettings({ favicon_url: newUrl });
      }
      toast.success('Favicon berhasil diperbarui', { id: toastId });
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Gagal mengunggah favicon';
      toast.error(message, { id: toastId });
    } finally {
      setUploadingFavicon(false);
      e.target.value = '';
    }
  };

  const triggerLogoInput = () => logoInputRef.current?.click();
  const triggerFaviconInput = () => faviconInputRef.current?.click();

  const ToggleSwitch: React.FC<{ value: boolean; onChange: (v: boolean) => void; label: string; description?: string }> = ({
    value, onChange, label, description,
  }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-neutral-text-primary">{label}</p>
        {description && <p className="text-xs text-neutral-text-secondary mt-0.5">{description}</p>}
      </div>
      <button onClick={() => onChange(!value)} className="shrink-0">
        {value ? (
          <ToggleRight className="w-10 h-6 text-teal-main" />
        ) : (
          <ToggleLeft className="w-10 h-6 text-neutral-text-secondary" />
        )}
      </button>
    </div>
  );

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Pengaturan Sistem' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Pengaturan Sistem</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Konfigurasi dan preferensi sistem</p>
        </div>
        {activeGroup !== 'maintenance' && (
          <Button variant="primary" onClick={handleSave} loading={saving} icon={<Save className="w-4 h-4" />}>
            Simpan Perubahan
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Navigation */}
        <div className="space-y-1">
          {settingGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => setActiveGroup(group.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${activeGroup === group.id
                ? 'bg-teal-main text-white shadow-md'
                : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-neutral-text-primary'
                }`}
            >
              {group.icon}
              <div>
                <p className="text-sm font-semibold">{group.label}</p>
                <p className={`text-xs ${activeGroup === group.id ? 'text-white/70' : 'text-neutral-text-secondary'}`}>
                  {group.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Right: Settings Form */}
        <div className="lg:col-span-3 space-y-6">
          {/* ===== GENERAL ===== */}
          {activeGroup === 'general' && (
            <>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Informasi Aplikasi</h3>
                <div className="space-y-4">
                  <Input label="Nama Aplikasi" value={settings.app_name} onChange={(e) => updateSetting('app_name', e.target.value)} />
                  <Input label="Deskripsi" value={settings.app_description} onChange={(e) => updateSetting('app_description', e.target.value)} />
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Informasi Institusi</h3>
                <div className="space-y-4">
                  <Input label="Nama Institusi" value={settings.institution_name} onChange={(e) => updateSetting('institution_name', e.target.value)} />

                  <Input
                    label="Kode Institusi"
                    value={settings.institution_code || ''}
                    onChange={(e) => updateSetting('institution_code', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                    placeholder="UBP-KRW"
                    helpText="Kode singkat untuk nomor dokumen resmi (contoh: 011/RAPIM/UBP-KRW/VI/2025)"
                  />

                  <Textarea label="Alamat" value={settings.institution_address} onChange={(e) => updateSetting('institution_address', e.target.value)} rows={2} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Telepon" value={settings.institution_phone} onChange={(e) => updateSetting('institution_phone', e.target.value)} />
                    <Input label="Email" type="email" value={settings.institution_email} onChange={(e) => updateSetting('institution_email', e.target.value)} />
                  </div>
                  <Input label="Website" value={settings.institution_website} onChange={(e) => updateSetting('institution_website', e.target.value)} />
                </div>
              </Card>
            </>
          )}

          {/* ===== NOTIFICATION ===== */}
          {activeGroup === 'notification' && (
            <>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Email</h3>
                <div className="space-y-4">
                  <ToggleSwitch value={settings.email_enabled} onChange={(v) => updateSetting('email_enabled', v)} label="Aktifkan Notifikasi Email" description="Kirim email untuk setiap perubahan status laporan" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Email Pengirim" value={settings.email_from} onChange={(e) => updateSetting('email_from', e.target.value)} />
                    <Input label="Nama Pengirim" value={settings.email_from_name} onChange={(e) => updateSetting('email_from_name', e.target.value)} />
                  </div>
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={handleTestEmail} loading={testingEmail}>
                      Tes Kirim Email
                    </Button>
                  </div>
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Jenis Notifikasi</h3>
                <div className="divide-y divide-neutral-border">
                  <ToggleSwitch value={settings.notif_submission} onChange={(v) => updateSetting('notif_submission', v)} label="Pengajuan Laporan" description="Notifikasi saat laporan baru diajukan" />
                  <ToggleSwitch value={settings.notif_validation} onChange={(v) => updateSetting('notif_validation', v)} label="Validasi" description="Notifikasi saat laporan divalidasi/ditolak" />
                  <ToggleSwitch value={settings.notif_rapim} onChange={(v) => updateSetting('notif_rapim', v)} label="RAPIM" description="Notifikasi jadwal dan hasil rapat pimpinan" />
                  <ToggleSwitch value={settings.notif_overdue} onChange={(v) => updateSetting('notif_overdue', v)} label="Overdue" description="Notifikasi saat laporan melewati target" />
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Pengingat</h3>
                <Input label="Hari Pengingat (pisahkan koma)" value={settings.notif_reminder_days} onChange={(e) => updateSetting('notif_reminder_days', e.target.value)} helpText="Contoh: 7,3,1 = pengingat H-7, H-3, H-1 sebelum target" />
              </Card>
            </>
          )}

          {/* ===== WORKFLOW ===== */}
          {activeGroup === 'workflow' && (
            <>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">SLA per Tingkat Risiko (hari)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input label="🟢 Rendah" type="number" value={settings.sla_rendah.toString()} onChange={(e) => updateSetting('sla_rendah', parseInt(e.target.value))} />
                  <Input label="🟡 Sedang" type="number" value={settings.sla_sedang.toString()} onChange={(e) => updateSetting('sla_sedang', parseInt(e.target.value))} />
                  <Input label="🟠 Tinggi" type="number" value={settings.sla_tinggi.toString()} onChange={(e) => updateSetting('sla_tinggi', parseInt(e.target.value))} />
                  <Input label="🔴 Kritis" type="number" value={settings.sla_critical.toString()} onChange={(e) => updateSetting('sla_critical', parseInt(e.target.value))} />
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Eskalasi & Arsip</h3>
                <div className="space-y-4">
                  <ToggleSwitch value={settings.auto_escalation} onChange={(v) => updateSetting('auto_escalation', v)} label="Eskalasi Otomatis" description="Otomatis eskalasi jika tidak ditindaklanjuti" />
                  <Input label="Eskalasi Setelah (hari)" type="number" value={settings.escalation_after_days.toString()} onChange={(e) => updateSetting('escalation_after_days', parseInt(e.target.value))} />
                  <Input label="Auto-arsip Setelah Selesai (hari)" type="number" value={settings.auto_archive_days.toString()} onChange={(e) => updateSetting('auto_archive_days', parseInt(e.target.value))} />
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Upload File</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Ukuran Maks per File (MB)" type="number" value={settings.max_file_size.toString()} onChange={(e) => updateSetting('max_file_size', parseInt(e.target.value))} />
                  <Input label="Maks File per Laporan" type="number" value={settings.max_files_per_report.toString()} onChange={(e) => updateSetting('max_files_per_report', parseInt(e.target.value))} />
                </div>
              </Card>
            </>
          )}

          {/* ===== SECURITY ===== */}
          {activeGroup === 'security' && (
            <>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Kebijakan Kata Sandi</h3>
                <div className="space-y-4">
                  <Input label="Panjang Minimum" type="number" value={settings.password_min_length.toString()} onChange={(e) => updateSetting('password_min_length', parseInt(e.target.value))} helpText="Minimal 6, maksimal 32 karakter" />
                  <div className="divide-y divide-neutral-border">
                    <ToggleSwitch value={settings.password_require_uppercase} onChange={(v) => updateSetting('password_require_uppercase', v)} label="Wajib Huruf Besar" description="Kata sandi harus mengandung minimal satu huruf kapital" />
                    <ToggleSwitch value={settings.password_require_number} onChange={(v) => updateSetting('password_require_number', v)} label="Wajib Angka" description="Kata sandi harus mengandung minimal satu angka" />
                    <ToggleSwitch value={settings.password_require_special} onChange={(v) => updateSetting('password_require_special', v)} label="Wajib Karakter Spesial" description="Kata sandi harus mengandung karakter seperti !@#$%^&*" />
                  </div>
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Sesi Login</h3>
                <div className="space-y-4">
                  <Input
                    label="Durasi Logout Otomatis (menit)"
                    type="number"
                    value={settings.session_lifetime.toString()}
                    onChange={(e) => updateSetting('session_lifetime', parseInt(e.target.value))}
                    helpText="Pengguna akan dikeluarkan otomatis setelah tidak aktif selama durasi ini"
                  />
                </div>
              </Card>
            </>
          )}

          {/* ===== APPEARANCE ===== */}
          {activeGroup === 'appearance' && (
            <>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Branding</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Logo Utama</label>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-neutral-bg border border-neutral-border flex items-center justify-center overflow-hidden">
                        {settings.logo_url ? (
                          <img src={getAssetUrl(settings.logo_url)} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-[10px] text-neutral-text-secondary">Logo</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" onClick={triggerLogoInput} loading={uploadingLogo} icon={<Upload className="w-4 h-4" />}>
                          Unggah Logo PNG
                        </Button>
                        <p className="text-xs text-neutral-text-secondary">PNG transparan, maksimal 2MB. Dipakai di header & login.</p>
                      </div>
                    </div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Favicon</label>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-neutral-bg border border-neutral-border flex items-center justify-center overflow-hidden">
                        {settings.favicon_url ? (
                          <img src={getAssetUrl(settings.favicon_url)} alt="Favicon" className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-[10px] text-neutral-text-secondary">Icon</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" onClick={triggerFaviconInput} loading={uploadingFavicon} icon={<Upload className="w-4 h-4" />}>
                          Unggah Favicon PNG
                        </Button>
                        <p className="text-xs text-neutral-text-secondary">PNG transparan, maksimal 2MB. Dipakai pada tab browser.</p>
                      </div>
                    </div>
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/png"
                      className="hidden"
                      onChange={handleFaviconChange}
                    />
                  </div>
                </div>
              </Card>
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Footer</h3>
                <Input label="Teks Footer" value={settings.footer_text} onChange={(e) => updateSetting('footer_text', e.target.value)} />
              </Card>
            </>
          )}

          {/* ===== MAINTENANCE ===== */}
          {activeGroup === 'maintenance' && (
            <div className="space-y-6">
              {/* System Info */}
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5" /> Informasi Sistem
                </h3>
                {systemInfo ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">Versi Aplikasi</p>
                      <p className="font-bold">{systemInfo.app_version}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">Laravel Version</p>
                      <p className="font-bold">{systemInfo.laravel_version}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">PHP Version</p>
                      <p className="font-bold">{systemInfo.php_version}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">Database Size</p>
                      <p className="font-bold">{systemInfo.db_size}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">Total Users</p>
                      <p className="font-bold">{systemInfo.total_users}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg">
                      <p className="text-neutral-text-secondary">Total Laporan</p>
                      <p className="font-bold">{systemInfo.total_laporan}</p>
                    </div>
                    <div className="p-3 bg-neutral-bg rounded-lg col-span-2">
                      <p className="text-neutral-text-secondary">Uptime</p>
                      <p className="font-bold">{systemInfo.uptime}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-neutral-text-secondary">Memuat informasi...</p>
                )}
              </Card>

              {/* Actions */}
              <Card>
                <h3 className="text-base font-bold text-teal-dark mb-4">Utilities</h3>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="outline"
                    onClick={() => handleMaintenanceAction('clearCache')}
                    disabled={maintenanceLoading}
                    icon={<RefreshCw className={`w-4 h-4 ${maintenanceLoading ? 'animate-spin' : ''}`} />}
                  >
                    Clear Cache
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleMaintenanceAction('migrate')}
                    disabled={maintenanceLoading}
                    icon={<Database className="w-4 h-4" />}
                  >
                    Run Migrations
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleMaintenanceAction('backup')}
                    disabled={maintenanceLoading}
                    icon={<HardDrive className="w-4 h-4" />}
                  >
                    Backup Database
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;

